import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, UploadCloud } from 'lucide-react';
import { useI18n } from '@/context/LanguageContext';

/**
 * Drag & drop + click + paste (Ctrl+V) image uploader.
 * onFiles(FileList|File[]) -> caller handles upload.
 */
export const ImageDrop = ({
  onFiles,
  multiple = false,
  busy = false,
  title,
  hint,
  testId = 'image-drop',
  compact = false,
  enablePaste = true,
}) => {
  const { t } = useI18n();
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const resolvedTitle = title || t('drop.defaultTitle');
  const resolvedHint = hint || t('drop.defaultHint');

  const handle = useCallback(
    (files) => {
      const arr = Array.from(files || []).filter((f) => f.type.startsWith('image/'));
      if (arr.length) onFiles(multiple ? arr : [arr[0]]);
    },
    [onFiles, multiple]
  );

  useEffect(() => {
    if (!enablePaste) return undefined;
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      const imgs = [];
      for (let i = 0; i < items.length; i += 1) {
        if (items[i].type?.startsWith('image/')) {
          const f = items[i].getAsFile();
          if (f) imgs.push(f);
        }
      }
      if (imgs.length) {
        e.preventDefault();
        handle(imgs);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [enablePaste, handle]);

  return (
    <div
      data-testid={testId}
      role="button"
      tabIndex={0}
      aria-label={t('drop.aria')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
      }}
      onClick={() => !busy && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        handle(e.dataTransfer.files);
      }}
      className={[
        'group cursor-pointer rounded-[var(--radius-lg)] border border-dashed text-center transition-colors duration-150',
        compact ? 'px-4 py-5' : 'px-6 py-10',
        drag
          ? 'border-[hsl(var(--brand-red))] bg-[hsl(var(--surface-2))]'
          : 'border-[hsl(var(--border-strong))] bg-[hsl(var(--surface))] hover:border-[hsl(var(--brand-red))]',
        busy ? 'pointer-events-none opacity-70' : '',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]',
      ].join(' ')}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => {
          handle(e.target.files);
          e.target.value = '';
        }}
      />
      <div className="flex flex-col items-center gap-2">
        <div className="grid h-11 w-11 place-items-center rounded-full bg-[hsl(var(--surface-2))] text-[hsl(var(--brand-red))] transition-colors duration-150 group-hover:bg-[hsl(var(--brand-red))] group-hover:text-white">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : multiple ? <ImagePlus className="h-5 w-5" /> : <UploadCloud className="h-5 w-5" />}
        </div>
        <p className="text-sm font-medium text-[hsl(var(--text))]">{busy ? t('drop.uploading') : resolvedTitle}</p>
        <p className="text-xs text-[hsl(var(--text-muted))]">{resolvedHint}</p>
      </div>
    </div>
  );
};
